import 'animate.css';

import { Map as MapIcon, Toc as TocIcon } from '@mui/icons-material';
import { ButtonGroup, Grid, Button as MButton } from '@mui/material';
import {
  HSpacer,
  RoutalPalette,
  RoutalSearchInputSelector,
  RoutalSelect,
  RoutalSelectMultiSelectOption,
  VSpacer,
} from '@nx-smartmonkey/ui';
import _ from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { Range } from 'react-date-range';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { SearchPredicate } from '@nx-smartmonkey/shared/domain';
import { dateFormatShortDate, removeStringDuplicates, toDayEnd } from '@nx-smartmonkey/shared/helpers';
import ExportIcon from '../../../assets/img/export.svg';
import WarningIcon from '../../../assets/svg/WarningIcon';
import { HeaderContent } from '../../../components/HeaderContent';
import { StopsActionsMenu } from '../../../components/menus/StopsActionsMenu';
import { StopsCreateMenu } from '../../../components/menus/StopsCreateMenu';
import { getVehicleColor } from '../../../constants/Colors';
import * as routing from '../../../constants/Routing';
import { Supervisor } from '../../../domain/supervisor/Supervisor';
import { VehicleFlat } from '../../../domain/vehicle/VehicleFlat';
import { intl } from '../../../helpers/IntGlobalProvider';
import { useRetrieveOrganization } from '../../../hooks/organization/useRetrieveOrganization';
import { useRetrieveActiveProject } from '../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActiveProjectGeoFences } from '../../../hooks/project/useRetrieveActiveProjectGeoFences';
import { useRetrieveRightPanelInfo } from '../../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useRetrieveFlatStops } from '../../../hooks/stop/useRetrieveFlatStops';
import { useSearchProjectStopsPredicates } from '../../../hooks/stop/useSearchProjectStopsPredicates';
import { useSetSelectedProjectStops } from '../../../hooks/stop/useSetSelectedProjectStops';
import { useRetrieveAllSupervisors } from '../../../hooks/supervisor/useRetrieveAllSupervisors';
import { useRetrieveSupportDocs } from '../../../hooks/support-docs/useRetrieveSupportDocs';
import { useRetrieveUser } from '../../../hooks/user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../../../hooks/user/useRetrieveUserCredentials';
import { useRetrieveFlatVehicles } from '../../../hooks/vehicle/useRetrieveFlatVehicles';
import Button from '../../../react-components/Button';
import { GeoFencesSelector } from '../../../react-components/GeoFencesSelector';
import Text from '../../../react-components/Text';
import { DateRangePicker } from '../../../react-components/date-range-picker/DateRangePicker';
import { StopRightPanel } from '../shared/right-panel/stop-right-panel/StopRightPanel';
import { ExportStopsDialog } from './ExportStopsDialog';
import { PlannerStopsUnderGorilla } from './PlannerStopsUnderGorilla';
import { PlannerStopsList } from './planner-stops-list';
import { PlannerStopsListColumnsButton } from './planner-stops-list/PlannerStopsListColumnsButton';
import { PlannerStopsMap } from './planner-stops-map';

const statusOptions = [`pending`, `canceled`, `completed`, `incomplete`] as const;

type StatusOptions = (typeof statusOptions)[number];

export const PlannerStops = () => {
  const navigate = useNavigate();
  const { retrieveDoc } = useRetrieveSupportDocs();
  const { data: user } = useRetrieveUser();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: organization } = useRetrieveOrganization();
  const { data: projectGeoFences } = useRetrieveActiveProjectGeoFences();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: projectVehicles } = useRetrieveFlatVehicles({ project: activeProject });
  const { data: projectSupervisors } = useRetrieveAllSupervisors();
  const { predicates, predicatesBase64, setPredicates } = useSearchProjectStopsPredicates();
  const location = useLocation();
  const { detailType } = useRetrieveRightPanelInfo();
  const { selectedStops } = useSetSelectedProjectStops();
  const [geoFenceFilters, setGeoFenceFilters] = useState<string[]>([]);
  const [datePickerPosition, setDatePickerPosition] = useState<{ top: number; left: number } | undefined>(undefined);
  const [isExportStopsDialogOpen, setIsExportStopsDialogOpen] = useState<boolean>(false);

  const filterStatus: Array<StatusOptions> = useMemo(() => {
    let newFilterStatus: Array<StatusOptions> = [];

    const filterStatusData = predicates.find((p) => p.field === `status`);
    if (!filterStatusData?.value || !Array.isArray(filterStatusData?.value)) {
      return [];
    }

    if ((filterStatusData!.value as string[]).length > 0) {
      // Filter only the valid status and remove duplicates.
      newFilterStatus = removeStringDuplicates(
        (filterStatusData.value as string[]).filter((filterStatusDataOption: string) =>
          statusOptions.includes(filterStatusDataOption as StatusOptions)
        )
      ) as Array<StatusOptions>;

      // If all status are selected, set the array to empty.
      if (newFilterStatus.length === statusOptions.length) {
        newFilterStatus = [];
      }
    }
    return newFilterStatus;
  }, [predicates]);

  const startDatePredicate = predicates.find((p) => p.field === `created_at` && p.operator === `gte`);
  const startDate = useMemo(
    () =>
      startDatePredicate?.value
        ? new Date(startDatePredicate.value as string)
        : moment(new Date()).subtract(`1`, `month`).toDate(),
    [startDatePredicate]
  );
  const endDatePredicate = predicates.find((p) => p.field === `created_at` && p.operator === `lte`);
  const endDate = useMemo(
    () => (endDatePredicate?.value ? new Date(endDatePredicate.value as string) : new Date()),
    [endDatePredicate]
  );

  const searchFields = [`label`, `reference_person`, `external_id`, `phone`, `email`];
  const searchTextPredicate = predicates.find((p) => searchFields.includes(p.field));
  const searchText = (searchTextPredicate?.value ?? ``) as string;
  const [searchField, setSearchField] = useState<string>(searchTextPredicate?.field || `label`);

  const vehicleIdPredicate = predicates.find((p) => p.field === `vehicle_id`);

  const filterVehicle: VehicleFlat | null | undefined =
    vehicleIdPredicate?.value !== `unassigned`
      ? projectVehicles?.find((v) => v.id === vehicleIdPredicate?.value)
      : null;

  const supervisorIdPredicate = predicates.find((p) => p.field === `supervisor_id`);

  const filterSupervisor: Supervisor | null | undefined =
    supervisorIdPredicate?.value !== `unassigned`
      ? projectSupervisors?.find((v) => v.id === supervisorIdPredicate?.value)
      : null;

  const { data: flatStops, run: retrieveFlatStops } = useRetrieveFlatStops({
    project: activeProject,
    geoFences: geoFenceFilters,
  });

  useEffect(() => {
    if (predicates.length > 0) {
      retrieveFlatStops();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject, geoFenceFilters]);

  if (!activeProject || !organization) {
    return null;
  }

  const isMapView = () =>
    location.pathname === routing.PLANNER.STOPS.MAP.replace(`:projectId`, activeProject?.id ?? ``);

  if (!userCredentials?.subscription.test(`planner.stops`)) {
    return <PlannerStopsUnderGorilla />;
  }

  return (
    <>
      <Grid
        container
        style={{
          height: `100%`,
          width: `100%`,
          zIndex: 2,
          backgroundColor: `white`,
          display: `flex`,
          flexDirection: `row`,
          minWidth: `1080px`,
        }}
        wrap="nowrap"
      >
        <div
          style={{
            height: `100%`,
            width: `100%`,
          }}
        >
          <Grid container direction="column" wrap="nowrap" style={{ height: `100%` }}>
            <Grid
              container
              direction="row"
              wrap="nowrap"
              alignItems="center"
              style={{
                padding: `20px`,
                paddingBottom: `12px`,
                borderBottom: `1px solid ${RoutalPalette.neutral10}`,
                backgroundColor: RoutalPalette.neutral00,
              }}
            >
              <HeaderContent
                titleIntlId="planner.stops.title"
                article={{
                  intlId: `planner.stops.header.article`,
                  link: retrieveDoc({ supportPage: `createStopsVideo`, language: user?.language.value }),
                }}
              />

              <Grid container item justifyContent="flex-end">
                {activeProject ? (
                  <StopsCreateMenu
                    id="list-stops-create-actions"
                    intlId="planner.stops.add_new"
                    style={{
                      backgroundColor: RoutalPalette.primary40,
                      justifyContent: `space-between`,
                      padding: `0 12px`,
                    }}
                    hideSeparator
                    containerStyle={{
                      width: `unset`,
                    }}
                  />
                ) : null}
              </Grid>
            </Grid>

            <Grid container>
              <Grid container direction="column" style={{ padding: `12px 20px` }}>
                <Grid container item justifyContent="space-between" alignItems="center" direction="row" wrap="nowrap">
                  <Grid container item direction="row">
                    <Grid item>
                      <Button
                        id="date-picker-button"
                        styleType="secondary"
                        variant="flat"
                        size="xs"
                        style={{
                          backgroundColor: `transparent`,
                          height: `26px`,
                          borderColor: RoutalPalette.neutral20,
                          borderRadius: `5px`,
                        }}
                        onClick={(e: any) => {
                          const datePickerButton = document.getElementById(`date-picker-button`);
                          if (!datePickerButton) return;

                          const { x, bottom } = datePickerButton.getBoundingClientRect();
                          setDatePickerPosition({ top: bottom, left: x });
                        }}
                      >
                        <Grid
                          container
                          item
                          justifyContent="space-between"
                          direction="row"
                          wrap="nowrap"
                          alignItems="center"
                        >
                          <Grid item>
                            <Text style={{ cursor: `pointer` }}>{`${dateFormatShortDate(
                              startDate
                            )} - ${dateFormatShortDate(endDate)}`}</Text>
                          </Grid>
                        </Grid>
                      </Button>
                    </Grid>

                    {userCredentials?.subscription.test(`planner.geofences`) && activeProject.geo_fences_enabled ? (
                      <>
                        <HSpacer medium />

                        <Grid item>
                          <GeoFencesSelector
                            geoFences={projectGeoFences ?? []}
                            selectedGeoFenceIds={geoFenceFilters}
                            selectButtonStyles={{
                              width: `240px`,
                            }}
                            noSelectedZonesText={
                              <Text
                                style={{ color: RoutalPalette.secondary40, cursor: `pointer` }}
                                intlId="planner.stops.no_zones_selected"
                              />
                            }
                            handleUpdate={(geoFences: string[]) => {
                              setGeoFenceFilters(geoFences);
                            }}
                          />
                        </Grid>
                      </>
                    ) : null}

                    <HSpacer medium />

                    <RoutalSelect
                      id="vehicle-select"
                      containerStyle={{
                        maxWidth: `220px`,
                        margin: `6px 0`,
                      }}
                      popoverProps={{
                        options: [
                          {
                            key: `not-assigned`,
                            listItemStyles: {
                              overflow: `hidden`,
                            },
                            onClick: () => {
                              setPredicates([...predicates.filter((p) => p.field !== `vehicle_id`)]);
                            },
                            content: (
                              <Grid
                                container
                                direction="row"
                                wrap="nowrap"
                                alignItems="center"
                                style={{ overflow: `hidden` }}
                              >
                                <Text
                                  tagName="span"
                                  style={{
                                    textAlign: `justify`,
                                    whiteSpace: `nowrap`,
                                    overflow: `hidden`,
                                    width: `100%`,
                                    textOverflow: `ellipsis`,
                                    cursor: `pointer`,
                                  }}
                                  intlId="none"
                                />
                              </Grid>
                            ),
                          },
                          ...(projectVehicles ?? []).map(
                            (vehicle: VehicleFlat): RoutalSelectMultiSelectOption => ({
                              key: `vehicle-${vehicle.id}`,
                              listItemStyles: {
                                overflow: `hidden`,
                                ...(filterVehicle?.id === vehicle.id ? { backgroundColor: `#E0E0E0` } : {}),
                              },
                              onClick: () => {
                                setPredicates([
                                  ...predicates.filter((p) => p.field !== `vehicle_id`),
                                  SearchPredicate.create({
                                    field: `vehicle_id`,
                                    operator: `eq`,
                                    type: `id`,
                                    value: vehicle.id,
                                  }),
                                ]);
                              },
                              content: (
                                <Grid
                                  container
                                  direction="row"
                                  wrap="nowrap"
                                  alignItems="center"
                                  style={{ overflow: `hidden` }}
                                >
                                  <Grid item>
                                    <div
                                      style={{
                                        width: `12px`,
                                        height: `12px`,
                                        borderRadius: `50%`,
                                        backgroundColor: getVehicleColor(vehicle, projectVehicles ?? []),
                                      }}
                                    />
                                  </Grid>
                                  <HSpacer small />
                                  <Text
                                    tagName="span"
                                    style={{
                                      textAlign: `justify`,
                                      whiteSpace: `nowrap`,
                                      overflow: `hidden`,
                                      width: `100%`,
                                      textOverflow: `ellipsis`,
                                      cursor: `pointer`,
                                    }}
                                  >
                                    {vehicle.label ? vehicle.label : intl.formatMessage({ id: `no_name` })}
                                  </Text>
                                </Grid>
                              ),
                            })
                          ),
                          {
                            key: `unassigned`,
                            listItemStyles: {
                              overflow: `hidden`,
                            },
                            onClick: () => {
                              setPredicates([
                                ...predicates.filter((p) => p.field !== `vehicle_id`),
                                SearchPredicate.create({
                                  field: `vehicle_id`,
                                  operator: `eq`,
                                  type: `id`,
                                  value: `unassigned`,
                                }),
                              ]);
                            },
                            content: (
                              <Grid container alignItems="center">
                                <WarningIcon color="primary" style={{ fontSize: `14px`, marginRight: `5px` }} />
                                <Text style={{ cursor: `pointer` }} intlId="vehicles_filter.unassigned" />
                              </Grid>
                            ),
                          },
                        ],
                      }}
                    >
                      <>
                        {!filterVehicle ? (
                          <>
                            {filterVehicle === null ? (
                              <Grid container alignItems="center">
                                <WarningIcon color="primary" style={{ fontSize: `14px`, marginRight: `5px` }} />
                                <Text style={{ cursor: `pointer` }} intlId="vehicles_filter.unassigned" />
                              </Grid>
                            ) : (
                              <Text
                                style={{
                                  cursor: `pointer`,
                                  width: `100%`,
                                  textAlign: `justify`,
                                }}
                                intlId="vehicles_filter.select_vehicle"
                              />
                            )}
                          </>
                        ) : (
                          <Grid
                            container
                            direction="row"
                            wrap="nowrap"
                            alignItems="center"
                            style={{ overflow: `hidden` }}
                          >
                            <Grid item>
                              <div
                                style={{
                                  width: `12px`,
                                  height: `12px`,
                                  borderRadius: `50%`,
                                  backgroundColor: getVehicleColor(filterVehicle, projectVehicles ?? []),
                                }}
                              />
                            </Grid>
                            <HSpacer small />
                            <Text
                              tagName="span"
                              style={{
                                textAlign: `justify`,
                                whiteSpace: `nowrap`,
                                overflow: `hidden`,
                                width: `100%`,
                                textOverflow: `ellipsis`,
                                cursor: `pointer`,
                              }}
                            >
                              {filterVehicle.label ? filterVehicle.label : intl.formatMessage({ id: `no_name` })}
                            </Text>
                          </Grid>
                        )}
                      </>
                    </RoutalSelect>

                    <HSpacer medium />

                    <RoutalSelect
                      id="status-filter"
                      containerStyle={{
                        maxWidth: `220px`,
                        margin: `6px 0`,
                      }}
                      popoverProps={{
                        options: statusOptions.map(
                          (status): RoutalSelectMultiSelectOption => ({
                            key: status,
                            checked: filterStatus.includes(status),
                            hideCheckIcon: false,
                            closeOnClick: false,
                            listItemStyles: {
                              overflow: `hidden`,
                            },
                            onClick: () => {
                              let newFilterStatus: Array<string> = filterStatus ? [...filterStatus] : [];
                              if (filterStatus.includes(status)) {
                                newFilterStatus = filterStatus.filter((filterStatus) => filterStatus !== status);
                              } else {
                                newFilterStatus.push(status);
                              }

                              setPredicates([
                                ...predicates.filter((p) => p.field !== `status`),
                                ...(newFilterStatus.length !== statusOptions.length && newFilterStatus.length > 0
                                  ? [
                                      SearchPredicate.create({
                                        field: `status`,
                                        operator: `in`,
                                        type: `array`,
                                        value: newFilterStatus,
                                      }),
                                    ]
                                  : []),
                              ]);
                            },
                            content: (
                              <Grid
                                container
                                direction="row"
                                wrap="nowrap"
                                alignItems="center"
                                style={{ overflow: `hidden` }}
                              >
                                <Text
                                  tagName="span"
                                  style={{
                                    textAlign: `justify`,
                                    whiteSpace: `nowrap`,
                                    overflow: `hidden`,
                                    width: `100%`,
                                    textOverflow: `ellipsis`,
                                    cursor: `pointer`,
                                  }}
                                  intlId={`planner.stops.status_filter.${status}`}
                                />
                              </Grid>
                            ),
                          })
                        ),
                      }}
                    >
                      {filterStatus.length === 0 ? (
                        <Text
                          style={{
                            cursor: `pointer`,
                            width: `100%`,
                            textAlign: `justify`,
                          }}
                          intlId={`planner.stops.status_filter.all`}
                        />
                      ) : (
                        <Text
                          style={{
                            cursor: `pointer`,
                            width: `100%`,
                            textAlign: `justify`,
                            overflow: `hidden`,
                            textOverflow: `ellipsis`,
                            whiteSpace: `nowrap`,
                          }}
                        >
                          {filterStatus.reduce((amount, status, index) => {
                            return `${amount}${index > 0 ? `,` : ``} ${intl.formatMessage({
                              id: `planner.stops.status_filter.${status}`,
                            })}`;
                          }, ``)}
                        </Text>
                      )}
                    </RoutalSelect>

                    {userCredentials?.subscription.test(`supervisors`) ? (
                      <>
                        <HSpacer medium />

                        <RoutalSelect
                          id="supervisor-select"
                          containerStyle={{
                            maxWidth: `220px`,
                            margin: `6px 0`,
                          }}
                          popoverProps={{
                            options: [
                              {
                                key: `not-assigned`,
                                listItemStyles: {
                                  overflow: `hidden`,
                                },
                                onClick: () => {
                                  setPredicates([...predicates.filter((p) => p.field !== `supervisor_id`)]);
                                },
                                content: (
                                  <Grid
                                    container
                                    direction="row"
                                    wrap="nowrap"
                                    alignItems="center"
                                    style={{ overflow: `hidden` }}
                                  >
                                    <Text
                                      tagName="span"
                                      style={{
                                        textAlign: `justify`,
                                        whiteSpace: `nowrap`,
                                        overflow: `hidden`,
                                        width: `100%`,
                                        textOverflow: `ellipsis`,
                                        cursor: `pointer`,
                                      }}
                                      intlId="none"
                                    />
                                  </Grid>
                                ),
                              },
                              ...(projectSupervisors ?? []).map(
                                (supervisor: Supervisor): RoutalSelectMultiSelectOption => ({
                                  key: `supervisor-${supervisor.id}`,
                                  listItemStyles: {
                                    overflow: `hidden`,
                                    ...(filterSupervisor?.id === supervisor.id ? { backgroundColor: `#E0E0E0` } : {}),
                                  },
                                  onClick: () => {
                                    setPredicates([
                                      ...predicates.filter((p) => p.field !== `supervisor_id`),
                                      SearchPredicate.create({
                                        field: `supervisor_id`,
                                        operator: `eq`,
                                        type: `id`,
                                        value: supervisor.id,
                                      }),
                                    ]);
                                  },
                                  content: (
                                    <Grid
                                      container
                                      direction="row"
                                      wrap="nowrap"
                                      alignItems="center"
                                      style={{ overflow: `hidden` }}
                                    >
                                      <Grid item>
                                        <div
                                          style={{
                                            width: `12px`,
                                            height: `12px`,
                                            borderRadius: `50%`,
                                            backgroundColor: supervisor.color?.value,
                                          }}
                                        />
                                      </Grid>
                                      <HSpacer small />
                                      <Text
                                        tagName="span"
                                        style={{
                                          textAlign: `justify`,
                                          whiteSpace: `nowrap`,
                                          overflow: `hidden`,
                                          width: `100%`,
                                          textOverflow: `ellipsis`,
                                          cursor: `pointer`,
                                        }}
                                      >
                                        {supervisor.name}
                                      </Text>
                                    </Grid>
                                  ),
                                })
                              ),
                            ],
                          }}
                        >
                          <>
                            {!filterSupervisor ? (
                              <Text
                                style={{
                                  cursor: `pointer`,
                                  width: `100%`,
                                  textAlign: `justify`,
                                }}
                                intlId="supervisors_filter.select_supervisor"
                              />
                            ) : (
                              <Grid
                                container
                                direction="row"
                                wrap="nowrap"
                                alignItems="center"
                                style={{ overflow: `hidden` }}
                              >
                                <Grid item>
                                  <div
                                    style={{
                                      width: `12px`,
                                      height: `12px`,
                                      borderRadius: `50%`,
                                      backgroundColor: filterSupervisor?.color?.value,
                                    }}
                                  />
                                </Grid>
                                <HSpacer small />
                                <Text
                                  tagName="span"
                                  style={{
                                    textAlign: `justify`,
                                    whiteSpace: `nowrap`,
                                    overflow: `hidden`,
                                    width: `100%`,
                                    textOverflow: `ellipsis`,
                                    cursor: `pointer`,
                                  }}
                                >
                                  {filterSupervisor.name}
                                </Text>
                              </Grid>
                            )}
                          </>
                        </RoutalSelect>
                      </>
                    ) : null}
                  </Grid>

                  <Grid
                    item
                    container
                    direction="row"
                    justifyContent="flex-end"
                    wrap="nowrap"
                    style={{ maxWidth: `400px` }}
                  >
                    <RoutalSearchInputSelector
                      id={`search-stops`}
                      onTextChange={_.debounce((text: any) => {
                        setPredicates([
                          ...predicates.filter((p) => !searchFields.includes(p.field)),
                          ...(text
                            ? [
                                SearchPredicate.create({
                                  field: searchField,
                                  operator: `contains`,
                                  type: `string`,
                                  value: text,
                                }),
                              ]
                            : []),
                        ]);
                      }, 300)}
                      searchText={searchText}
                      options={searchFields.map(
                        (field): RoutalSelectMultiSelectOption => ({
                          key: `search-selector-button-${field}`,
                          width: 250,
                          checked: field === searchField,
                          content: <Text intlId={`properties.stop.${field}`} />,
                          onClick: () => {
                            setSearchField(field);
                            const predicate = predicates.filter((p) => searchFields.includes(p.field));
                            const text = predicate.length > 0 ? predicate[0].value : undefined;
                            if (text) {
                              setPredicates([
                                ...predicates.filter((p) => !searchFields.includes(p.field)),
                                ...(text
                                  ? [
                                      SearchPredicate.create({
                                        field,
                                        operator: `contains`,
                                        type: `string`,
                                        value: text,
                                      }),
                                    ]
                                  : []),
                              ]);
                            }
                          },
                        })
                      )}
                    />
                  </Grid>
                </Grid>

                <VSpacer small />

                <Grid container item justifyContent="space-between" direction="row" wrap="nowrap">
                  <Grid container alignItems="center">
                    {selectedStops.length === 0 ? (
                      <Text intlId="planner.stops.num_stops" intlValues={{ total: (flatStops ?? []).length }} />
                    ) : (
                      <Text
                        intlId="planner.stops.num_selected_stops"
                        intlValues={{ num: selectedStops.length, total: (flatStops ?? []).length }}
                      />
                    )}

                    <HSpacer medium />

                    <Grid item>
                      {(flatStops ?? []).filter((stopFlat) => selectedStops.includes(stopFlat.id)).length > 0 ? (
                        <StopsActionsMenu
                          type="popover"
                          stops={(flatStops ?? []).filter((stopFlat) => selectedStops.includes(stopFlat.id))}
                          showAssignToDriver={false}
                          showOpenPlan={true}
                          showChangeConstraints={false}
                          showOpenCustomers={userCredentials?.subscription.test(`customers`)}
                          popoverProps={{
                            hideArrow: false,
                            style: {
                              justifyContent: `space-between`,
                            },
                            containerStyle: {
                              width: `200px`,
                            },
                            content: (
                              <Text
                                variant="body"
                                intlId="plans.plan.data.services.header.actions"
                                style={{ cursor: `pointer` }}
                              />
                            ),
                          }}
                          afterDeleteStops={(deletedStopsIds: string[]) => {
                            retrieveFlatStops();
                          }}
                        />
                      ) : null}
                    </Grid>
                  </Grid>

                  <Grid item container alignItems="center" justifyContent="flex-end">
                    <PlannerStopsListColumnsButton id="stops-configurable-table-columns" />
                    <HSpacer small />
                    <Button
                      styleType="secondary"
                      disabled={selectedStops.length === 0}
                      onClick={() => setIsExportStopsDialogOpen(true)}
                      style={{
                        height: `36px`,
                        width: `32px`,
                        padding: `0 4px`,
                        margin: 0,
                        borderRadius: `5px`,
                      }}
                    >
                      <img alt="" height="24px" src={ExportIcon} draggable="false" />
                    </Button>
                    <HSpacer small />
                    <ButtonGroup
                      disableElevation
                      disableRipple
                      disableFocusRipple
                      variant="contained"
                      style={{ height: `36px` }}
                    >
                      <MButton
                        color="secondary"
                        style={{
                          backgroundColor: !isMapView() ? RoutalPalette.secondary40 : RoutalPalette.secondary00,
                          borderBottomLeftRadius: `5px`,
                          borderTopLeftRadius: `5px`,
                        }}
                        onClick={() => {
                          navigate(
                            `${routing.PLANNER.STOPS.LIST.replace(`:projectId`, activeProject?.id ?? ``)}${
                              predicatesBase64 ? `?predicates=${predicatesBase64}` : ``
                            }`
                          );
                        }}
                      >
                        <TocIcon style={{ fontSize: `20px` }} />
                      </MButton>
                      <MButton
                        color="secondary"
                        style={{
                          backgroundColor: isMapView() ? RoutalPalette.secondary40 : RoutalPalette.secondary00,
                          borderBottomRightRadius: `5px`,
                          borderTopRightRadius: `5px`,
                        }}
                        onClick={() => {
                          navigate(
                            `${routing.PLANNER.STOPS.MAP.replace(`:projectId`, activeProject?.id ?? ``)}${
                              predicatesBase64 ? `?predicates=${predicatesBase64}` : ``
                            }`
                          );
                        }}
                      >
                        <MapIcon style={{ fontSize: `20px` }} />
                      </MButton>
                    </ButtonGroup>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Routes>
              <Route
                index
                element={<Navigate to={routing.PLANNER.STOPS.LIST.replace(`:projectId`, activeProject?.id ?? ``)} />}
              />
              <Route path="/list" element={<PlannerStopsList geoFenceFilters={geoFenceFilters} />} />
              <Route
                path="/map"
                element={<PlannerStopsMap flatStops={flatStops ?? []} geoFenceFilters={geoFenceFilters} />}
              />
              <Route
                path="*"
                element={<Navigate to={routing.PLANNER.STOPS.LIST.replace(`:projectId`, activeProject?.id ?? ``)} />}
              />
            </Routes>
          </Grid>
        </div>

        {detailType === `stop` ? <StopRightPanel selectedStopsIds={selectedStops} validatePlan={false} /> : null}
      </Grid>

      <DateRangePicker
        id="select-dates"
        popoverPosition={datePickerPosition}
        startDate={startDate ? new Date(startDate) : undefined}
        endDate={endDate ? new Date(endDate) : undefined}
        onClose={() => {
          setDatePickerPosition(undefined);
        }}
        onUpdate={(range: Range) => {
          setPredicates([
            ...predicates.filter((p) => p.field !== `created_at`),
            ...(range.startDate
              ? [
                  SearchPredicate.create({
                    field: `created_at`,
                    operator: `gte`,
                    type: `date`,
                    value: range.startDate.toUTCString(),
                  }),
                ]
              : []),
            ...(range.endDate
              ? [
                  SearchPredicate.create({
                    field: `created_at`,
                    operator: `lte`,
                    type: `date`,
                    value: toDayEnd(range.endDate).toUTCString(),
                  }),
                ]
              : []),
          ]);
        }}
      />

      <ExportStopsDialog
        open={isExportStopsDialogOpen}
        onCloseDialog={() => setIsExportStopsDialogOpen(false)}
        selectedStopIds={selectedStops}
      />
    </>
  );
};
